import { useContext, useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Button,
  Text,
  Flex,
  useToast,
  Divider,
  FormControl,
  FormLabel,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Collapse,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Switch,
  SimpleGrid,
  Circle,
  Checkbox,
  CheckboxGroup,
  RadioGroup,
  Radio,
  Tooltip,
  Icon,
  Select,
  Input,
} from '@chakra-ui/react';
import { useBillingService } from '../../../services/billingService';
import { useBilling } from '../../../store/client/BillingContext';
import { MeContext } from '../../../context/MeContext';
import CustomToast from '../../../common/CustomToast';
import { Controller, useForm } from 'react-hook-form';
import { CustomInfoOutlineIcon } from '../../../theme/icons/CustomInfoOutlineIcon';

const SSO_PROVIDERS = [
  { value: 'google', label: 'Google' },
  { value: 'microsoft', label: 'Microsoft' },
  { value: 'saml', label: 'SAML' },
  { value: 'other', label: 'Other (please specify)' },
];

const INITIAL_FORM_STATE = {
  templates: 5,
  passes: 100000,
  entities: 1,
  seats: 5,
  readerLogins: 10,
  localisation: false,
  watermark: false,
  pass_binding: false,
  sso: false,
  ssoProvider: '',
  ssoCustomProvider: '',
  notes: '',
  entitiesRequired: false,
};

const PILOT_FORM_STATE = {
  ...INITIAL_FORM_STATE,
  templates: 1,
  passes: 500,
  entities: 1,
  seats: 1,
  readerLogins: 1,
};

const NUMBER_FIELDS = [
  {
    key: 'seats',
    label: 'How many team members will need access?',
    min: 1,
    max: 30,
    step: 1,
  },
  {
    key: 'passes',
    label: 'Roughly how many passes do you expect to issue in total?',
    min: 500,
    max: 5000000,
    step: 1000,
  },
  {
    key: 'templates',
    label: 'How many different pass templates will you need?',
    min: 1,
    max: 50,
    step: 1,
  },
  {
    key: 'readerLogins',
    label:
      'If you plan to use the PassEntry Reader app to scan passes, how many devices will you use?',
    min: 1,
    max: 50,
    step: 1,
  },
];

const clampNumber = (value, minVal, maxVal) => {
  const n = Number(value);
  if (Number.isNaN(n)) return minVal ?? 0;
  if (maxVal != null && n > maxVal) return maxVal;
  if (minVal != null && n < minVal) return minVal;
  return n;
};

const NumberFieldWithStepper = ({ value, onChange, min, max, step }) => {
  const [localValue, setLocalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) setLocalValue(value);
  }, [value, isFocused]);

  const displayValue = isFocused ? localValue : value;

  const handleChange = (_, valueAsNumber) => {
    setLocalValue(Number.isNaN(valueAsNumber) ? '' : valueAsNumber);
  };

  const handleBlur = () => {
    const committed = clampNumber(
      localValue === '' ? min : localValue,
      min,
      max
    );
    setLocalValue(committed);
    setIsFocused(false);
    onChange(committed);
  };

  return (
    <NumberInput
      width="120px"
      value={displayValue}
      onChange={handleChange}
      onFocus={() => setIsFocused(true)}
      onBlur={handleBlur}
      min={min}
      max={max}
      step={step}
    >
      <NumberInputField bg="softWhite" />
      <NumberInputStepper>
        <NumberIncrementStepper />
        <NumberDecrementStepper />
      </NumberInputStepper>
    </NumberInput>
  );
};

const BOOLEAN_FIELDS = [
  {
    key: 'localisation',
    label: 'Multi-language support',
    tooltip:
      "Localisation allows you to add translations for pass content, automatically displayed based on the customer's device language.",
  },
  {
    key: 'watermark',
    label: 'Disable watermark',
    tooltip:
      'Disabling the watermark will remove it from all non-NFC passes. For NFC passes, the watermark will be removed from Google passes only.',
  },
  {
    key: 'pass_binding',
    label: 'Pass Binding',
    tooltip:
      'Restrict passes to a specific wallet account, preventing sharing and unauthorized use. Barcodes are prohibited when binding is enabled.',
  },
  {
    key: 'sso',
    label: 'Single Sign-On (SSO)',
    tooltip:
      "Single Sign-On allows your team to log in using your organisation's identity provider.",
  },
];

const toBoolean = (v) => {
  if (v === true || v === 'true' || v === 1) return true;
  if (v === false || v === 'false' || v === 0 || v === '') return false;
  return Boolean(v);
};

const ENTITLEMENT_KEY_TO_FORM_KEY = {
  pass_templates: 'templates',
  passes: 'passes',
  entities: 'entities',
  seats: 'seats',
  passentry_reader_logins: 'readerLogins',
  localisation: 'localisation',
  passentry_watermark: 'watermark',
  pass_binding: 'pass_binding',
  sso: 'sso',
  sso_provider: 'ssoProvider',
};

const mapSubscriptionEntitlementsArrayToFormValues = (entitlementsArray) => {
  if (!Array.isArray(entitlementsArray) || entitlementsArray.length === 0) {
    return INITIAL_FORM_STATE;
  }
  const byKey = {};
  entitlementsArray.forEach((ent) => {
    const formKey = ENTITLEMENT_KEY_TO_FORM_KEY[ent.key] ?? ent.key;
    if (
      (ent.kind === 'countLimit' || ent.kind === 'softLimit') &&
      ent.limitValue != null
    ) {
      byKey[formKey] = Number(ent.limitValue);
    } else if (ent.kind === 'access' && ent.valueBool != null) {
      byKey[formKey] = toBoolean(ent.valueBool);
    } else if (ent.kind === 'enforcement' && ent.valueBool != null) {
      const raw = toBoolean(ent.valueBool);
      byKey[formKey] = ent.key === 'passentry_watermark' ? !raw : raw;
    }
  });
  const entities = byKey.entities ?? INITIAL_FORM_STATE.entities;
  const ssoProvider = byKey.ssoProvider ?? INITIAL_FORM_STATE.ssoProvider;
  const isSso = toBoolean(byKey.sso ?? INITIAL_FORM_STATE.sso);
  const isKnownProvider = SSO_PROVIDERS.some(
    (p) => p.value !== 'other' && p.value === ssoProvider
  );
  return {
    templates: byKey.templates ?? INITIAL_FORM_STATE.templates,
    passes: byKey.passes ?? INITIAL_FORM_STATE.passes,
    entities,
    seats: byKey.seats ?? INITIAL_FORM_STATE.seats,
    readerLogins: byKey.readerLogins ?? INITIAL_FORM_STATE.readerLogins,
    localisation: toBoolean(
      byKey.localisation ?? INITIAL_FORM_STATE.localisation
    ),
    watermark: toBoolean(byKey.watermark ?? INITIAL_FORM_STATE.watermark),
    pass_binding: toBoolean(
      byKey.pass_binding ?? INITIAL_FORM_STATE.pass_binding
    ),
    sso: isSso,
    ssoProvider:
      isSso && ssoProvider ? (isKnownProvider ? ssoProvider : 'other') : '',
    ssoCustomProvider:
      isSso && ssoProvider && !isKnownProvider ? ssoProvider : '',
    notes: INITIAL_FORM_STATE.notes,
    entitiesRequired: entities > 1,
  };
};

const mapEntitlementsToFormValues = (entitlements) => {
  if (!entitlements) return INITIAL_FORM_STATE;
  const entities = entitlements.entities ?? INITIAL_FORM_STATE.entities;
  const ssoProvider =
    entitlements.ssoProvider ?? entitlements.sso_provider ?? '';
  const isSso = toBoolean(entitlements.sso ?? INITIAL_FORM_STATE.sso);
  const isKnownProvider = SSO_PROVIDERS.some(
    (p) => p.value !== 'other' && p.value === ssoProvider
  );
  return {
    templates: entitlements.templates ?? INITIAL_FORM_STATE.templates,
    passes: entitlements.passes ?? INITIAL_FORM_STATE.passes,
    entities,
    seats: entitlements.seats ?? INITIAL_FORM_STATE.seats,
    readerLogins: entitlements.readerLogins ?? INITIAL_FORM_STATE.readerLogins,
    localisation: toBoolean(
      entitlements.localisation ?? INITIAL_FORM_STATE.localisation
    ),
    watermark: toBoolean(
      entitlements.watermark ?? INITIAL_FORM_STATE.watermark
    ),
    pass_binding: toBoolean(
      entitlements.pass_binding ?? INITIAL_FORM_STATE.pass_binding
    ),
    sso: isSso,
    ssoProvider:
      isSso && ssoProvider ? (isKnownProvider ? ssoProvider : 'other') : '',
    ssoCustomProvider:
      isSso && ssoProvider && !isKnownProvider ? ssoProvider : '',
    notes: entitlements.notes ?? INITIAL_FORM_STATE.notes,
    entitiesRequired: entities > 1,
  };
};

const RequestCustomPlanModal = ({
  isOpen,
  onClose,
  onSuccess,
  initialValues,
  amendPlanId,
  subscription,
  selectedInterval,
}) => {
  const billingService = useBillingService();
  const { refresh, requestAmendment } = useBilling();
  const { state } = useContext(MeContext);
  const watermarkFree = state?.watermarkFree;
  const isOnTrial = Boolean(
    subscription?.onTrial || state?.organisationStatus === 'trial'
  );
  const booleanFields = watermarkFree
    ? BOOLEAN_FIELDS.filter(({ key }) => key !== 'watermark')
    : BOOLEAN_FIELDS;
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const [page, setPage] = useState(1);
  const isAmendMode = Boolean(amendPlanId);

  const getDefaultFormValues = () => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      return mapEntitlementsToFormValues(initialValues);
    }
    if (isOnTrial) {
      return PILOT_FORM_STATE;
    }
    if (subscription?.entitlements?.length) {
      return mapSubscriptionEntitlementsArrayToFormValues(
        subscription.entitlements
      );
    }
    return INITIAL_FORM_STATE;
  };

  const defaultValues = getDefaultFormValues();

  const { control, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues,
  });

  const handleClose = () => {
    setPage(1);
    reset(defaultValues);
    onClose?.();
  };

  const showToast = (status, title, description) => {
    toast({
      render: (props) => (
        <CustomToast
          status={status}
          title={title}
          description={description}
          onClose={props.onClose}
          id={props.id}
        />
      ),
    });
  };

  const onSubmit = async (formValues) => {
    setLoading(true);

    try {
      const ssoProvider = formValues.sso
        ? formValues.ssoProvider === 'other'
          ? formValues.ssoCustomProvider
          : formValues.ssoProvider
        : '';

      const requestedEntitlements = {
        templates: formValues.templates,
        passes: formValues.passes,
        entities: formValues.entities,
        seats: formValues.seats,
        reader_logins: formValues.readerLogins,
        localisation: formValues.localisation,
        ...(watermarkFree ? {} : { watermark: formValues.watermark }),
        pass_binding: formValues.pass_binding,
        sso: formValues.sso,
        sso_provider: ssoProvider,
      };

      if (isAmendMode) {
        await requestAmendment(
          amendPlanId,
          requestedEntitlements,
          formValues.notes
        );
        showToast(
          'success',
          'Amendment requested',
          "We'll review your changes and update your plan offer."
        );
      } else {
        const response = await billingService.createCustomPlanRequest(
          requestedEntitlements,
          formValues.notes,
          selectedInterval
        );
        await refresh();

        const createdRequest = response?.custom_plan_request ?? response;
        const isAutoOffered =
          createdRequest?.autoOffered ?? createdRequest?.auto_offered ?? false;

        showToast(
          'success',
          isAutoOffered ? 'Your custom plan is ready' : 'Request received',
          isAutoOffered
            ? 'Review the offer above and accept it when you’re ready.'
            : 'We’ll review it and get back to you within one working day. Your current plan stays active in the meantime.'
        );
      }

      handleClose();
      onSuccess?.();
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors?.[0] || 'Something went wrong';

      showToast('error', 'Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setPage(1);
      reset(getDefaultFormValues());
    }
  }, [isOpen, reset, initialValues, subscription?.entitlements, isOnTrial]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size="2xl">
        <ModalOverlay />
        <ModalContent pt={12} px={10}>
          <ModalCloseButton />
          <Text textStyle="heading3" textAlign="center" mb={3}>
            {isAmendMode ? 'Request plan changes' : 'Request a custom plan'}
          </Text>
          <Text textStyle="bodyMedium" textAlign="center">
            {isAmendMode
              ? "Tell us what you'd like to change and we'll update the plan for you. Your current plan will stay active until you accept a new one."
              : 'Give us a rough idea of your expected usage, features and support needs.'}
          </Text>
          <Divider borderColor="primaryBackground" mt={6} />
          <ModalBody py={6}>
            <Flex direction="column" gap={6}>
              {page === 1 && (
                <>
                  <SimpleGrid columns={1} spacing={4}>
                    {NUMBER_FIELDS.map(({ key, label, min, step, max }) => (
                      <Controller
                        key={key}
                        name={key}
                        control={control}
                        render={({ field }) => (
                          <FormControl>
                            <FormLabel mb={4} variant="templateBuilderLabel">
                              {label}
                            </FormLabel>
                            <Flex alignItems="center" gap={6}>
                              <Slider
                                flex="1"
                                value={field.value}
                                onChange={(value) => field.onChange(value)}
                                min={min}
                                max={max}
                                step={step}
                              >
                                <SliderTrack>
                                  <SliderFilledTrack />
                                </SliderTrack>
                                <SliderThumb />
                              </Slider>
                              <NumberFieldWithStepper
                                value={field.value}
                                onChange={(v) => field.onChange(v)}
                                min={min}
                                max={max}
                                step={step}
                              />
                            </Flex>
                          </FormControl>
                        )}
                      />
                    ))}
                  </SimpleGrid>
                </>
              )}

              {page === 2 && (
                <>
                  <FormControl>
                    <FormLabel mb={4} variant="templateBuilderLabel">
                      Which advanced features do you need?
                    </FormLabel>
                    <CheckboxGroup
                      value={booleanFields
                        .filter(({ key }) => watch(key))
                        .map(({ key }) => key)}
                      onChange={(selected) => {
                        booleanFields.forEach(({ key }) => {
                          setValue(key, selected.includes(key), {
                            shouldDirty: true,
                          });
                        });
                        if (!selected.includes('sso')) {
                          setValue('ssoProvider', '');
                          setValue('ssoCustomProvider', '');
                        }
                      }}
                    >
                      <Flex direction="column" gap={2}>
                        {booleanFields.map(({ key, label, tooltip }) => (
                          <Checkbox
                            key={key}
                            value={key}
                            textStyle="bodySmall"
                            size="md"
                          >
                            <Tooltip
                              label={tooltip}
                              hasArrow
                              placement="top"
                              fontSize="12px"
                              bg="inkBlue"
                              color="white"
                              borderRadius="6px"
                              textAlign="center"
                              p="10px"
                            >
                              <Flex alignItems="center" gap={2}>
                                <Text textStyle="bodyMedium">{label}</Text>
                                <Icon
                                  as={CustomInfoOutlineIcon}
                                  boxSize={4}
                                  fill="grey"
                                />
                              </Flex>
                            </Tooltip>
                          </Checkbox>
                        ))}
                      </Flex>
                    </CheckboxGroup>
                  </FormControl>

                  <Collapse in={watch('sso')}>
                    <Flex direction="column" gap={4}>
                      <FormControl>
                        <FormLabel mb={2} variant="templateBuilderLabel">
                          Which SSO provider do you use?
                        </FormLabel>
                        <Select
                          bg="softWhite"
                          placeholder="Select a provider"
                          value={watch('ssoProvider')}
                          onChange={(e) => {
                            setValue('ssoProvider', e.target.value, {
                              shouldDirty: true,
                            });
                            if (e.target.value !== 'other') {
                              setValue('ssoCustomProvider', '');
                            }
                          }}
                        >
                          {SSO_PROVIDERS.map(({ value, label }) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </Select>
                      </FormControl>

                      <Collapse in={watch('ssoProvider') === 'other'}>
                        <FormControl>
                          <FormLabel mb={2} variant="templateBuilderLabel">
                            Please specify your SSO provider
                          </FormLabel>
                          <Input
                            bg="softWhite"
                            value={watch('ssoCustomProvider')}
                            onChange={(e) =>
                              setValue('ssoCustomProvider', e.target.value)
                            }
                            placeholder="e.g. Okta, Auth0, OneLogin"
                          />
                        </FormControl>
                      </Collapse>
                    </Flex>
                  </Collapse>

                  <FormControl>
                    <FormLabel mb={4} variant="templateBuilderLabel">
                      Will you manage multiple brands, organisations or clients?
                    </FormLabel>
                    <RadioGroup
                      value={watch('entitiesRequired') ? 'true' : 'false'}
                      onChange={(value) => {
                        setValue('entitiesRequired', value === 'true');
                        value === 'false' ? setValue('entities', 1) : null;
                      }}
                    >
                      <Flex direction="column" gap={2}>
                        <Radio value="true">Yes</Radio>
                        <Radio value="false">No</Radio>
                      </Flex>
                    </RadioGroup>
                  </FormControl>
                  <Collapse in={watch('entitiesRequired')}>
                    <FormControl>
                      <FormLabel mb={4} variant="templateBuilderLabel">
                        How many brands, organisations or clients will you
                        manage?
                      </FormLabel>
                      <NumberFieldWithStepper
                        value={watch('entities')}
                        onChange={(v) => setValue('entities', v)}
                        min={2}
                        max={50}
                        step={1}
                      />
                    </FormControl>
                  </Collapse>

                  <FormControl>
                    <FormLabel mb={4} variant="templateBuilderLabel">
                      Anything else we should know?
                    </FormLabel>
                    <Textarea
                      bg="softWhite"
                      value={watch('notes')}
                      onChange={(e) => setValue('notes', e.target.value)}
                      placeholder="Special requirements, timelines, integrations or anything else that might affect pricing."
                      rows={4}
                    />
                  </FormControl>
                </>
              )}

              {page === 1 && (
                <Flex justifyContent="flex-end" mt={8}>
                  <Button
                    variant="gradient"
                    w="fit-content"
                    onClick={() => setPage(2)}
                  >
                    Next
                  </Button>
                </Flex>
              )}
              {page === 2 && (
                <Flex
                  direction="row"
                  mt={4}
                  gap={4}
                  justifyContent="space-between"
                >
                  <Button variant="outline" onClick={() => setPage(1)}>
                    Back
                  </Button>
                  <Button
                    variant="gradient"
                    onClick={handleSubmit(onSubmit)}
                    isLoading={loading}
                    loadingText="Submitting..."
                  >
                    Submit
                  </Button>
                </Flex>
              )}
              <Flex justifyContent="center" mt={4} gap={1.5}>
                <Circle
                  size="12px"
                  bg={page === 1 ? 'brand' : 'lightGrey'}
                  opacity={0.7}
                ></Circle>
                <Circle
                  size="12px"
                  bg={page === 2 ? 'brand' : 'lightGrey'}
                  opacity={0.7}
                ></Circle>
              </Flex>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default RequestCustomPlanModal;
